/**
 * ReportsPage - Client-facing reports page
 * 
 * Allows clients to:
 * - View list of reports generated for their tenant
 * - Download reports as HTML or ZIP
 * - See report history with generation dates
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  FileText,
  Download,
  Calendar,
  Clock,
  FileArchive,
  Image as ImageIcon,
  Eye,
  RefreshCw,
  FileBarChart,
  ChevronRight,
  Camera,
  CheckSquare,
  Square,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReportsPage() {
  const { sessionToken, user } = useAuth();
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [generating, setGenerating] = useState({});
  
  // Page export state
  const [exportablePages, setExportablePages] = useState([]);
  const [selectedExportPages, setSelectedExportPages] = useState(new Set());
  const [exportStartDate, setExportStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [exportEndDate, setExportEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState("");

  // Fetch reports and templates
  useEffect(() => {
    if (sessionToken) {
      fetchReports();
      fetchTemplates();
      fetchExportablePages();
    }
  }, [sessionToken]);

  const fetchExportablePages = async () => {
    try {
      const response = await axios.get(`${API}/reports/exportable-pages`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      const pages = response.data.pages || [];
      setExportablePages(pages);
      setSelectedExportPages(new Set(pages.map(p => p.page_id)));
    } catch (error) {
      console.error("Error fetching exportable pages:", error);
    }
  };

  const toggleExportPage = (pageId) => {
    setSelectedExportPages(prev => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  };

  const toggleAllExportPages = () => {
    if (selectedExportPages.size === exportablePages.length) {
      setSelectedExportPages(new Set());
    } else {
      setSelectedExportPages(new Set(exportablePages.map(p => p.page_id)));
    }
  };

  const exportPages = async () => {
    if (selectedExportPages.size === 0) {
      toast.error("Select at least one page to export");
      return;
    }

    setExporting(true);
    setExportProgress(`Capturing ${selectedExportPages.size} pages...`);

    try {
      const response = await axios.post(
        `${API}/reports/export-pages`,
        {
          start_date: exportStartDate,
          end_date: exportEndDate,
          pages: Array.from(selectedExportPages),
          viewport_width: 1440,
          viewport_height: 900,
          full_page: true,
          wait_seconds: 6
        },
        {
          params: { Authorization: `Bearer ${sessionToken}` },
          responseType: 'blob',
          timeout: 300000 // 5 min timeout
        }
      );

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Page_Export_${exportStartDate}_to_${exportEndDate}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedExportPages.size} pages`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export pages. This may take a while for many pages.");
    } finally {
      setExporting(false);
      setExportProgress("");
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/history`, {
        params: { Authorization: `Bearer ${sessionToken}`, limit: 50 }
      });
      setReports(response.data.reports || []);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/reports/templates`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  // View HTML report in new tab
  const viewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/download/${reportId}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      
      // Open HTML in new tab
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Failed to open report");
    }
  };

  // Download report as HTML
  const downloadHtml = async (report) => {
    setDownloading(prev => ({ ...prev, [report.report_id]: 'html' }));
    try {
      const response = await axios.get(`${API}/reports/download/${report.report_id}`, {
        params: { Authorization: `Bearer ${sessionToken}` }
      });
      
      const blob = new Blob([response.data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report.template_name || 'Report'}_${report.generated_at?.split('T')[0]}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Report downloaded");
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setDownloading(prev => ({ ...prev, [report.report_id]: null }));
    }
  };

  // Download full ZIP with charts
  const downloadZip = async (templateId, templateName) => {
    setDownloading(prev => ({ ...prev, [templateId]: 'zip' }));
    try {
      const response = await axios.post(
        `${API}/reports/export-full/${templateId}`,
        {},
        {
          params: { Authorization: `Bearer ${sessionToken}` },
          responseType: 'blob',
          timeout: 120000 // 2 min timeout for chart generation
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${templateName?.replace(/\s+/g, '_') || 'Report'}_Full.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Report package downloaded");
    } catch (error) {
      toast.error("Failed to download report package");
    } finally {
      setDownloading(prev => ({ ...prev, [templateId]: null }));
    }
  };

  // Generate new report from template
  const generateReport = async (template) => {
    setGenerating(prev => ({ ...prev, [template.template_id]: true }));
    try {
      const response = await axios.post(
        `${API}/reports/generate`,
        { template_id: template.template_id, format: "html" },
        { params: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      toast.success("Report generated successfully");
      fetchReports(); // Refresh the list
      
      // Open the new report
      if (response.data.html) {
        const blob = new Blob([response.data.html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      }
    } catch (error) {
      toast.error("Failed to generate report");
    } finally {
      setGenerating(prev => ({ ...prev, [template.template_id]: false }));
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get relative time
  const getRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <FileBarChart className="w-7 h-7 text-purple-600" />
            Reports
          </h1>
          <p className="text-slate-500 mt-1">View and download your analytics reports</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchReports(); fetchTemplates(); }}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Page Export */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Export Page Screenshots
          </CardTitle>
          <p className="text-sm text-slate-500">
            Capture exact copies of all pages as high-quality PNG images with a specific reporting period.
          </p>
        </CardHeader>
        <CardContent>
          {/* Date Range */}
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Start Date</label>
              <input
                type="date"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                className="border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="export-start-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">End Date</label>
              <input
                type="date"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                className="border border-slate-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="export-end-date"
              />
            </div>
            <Button
              onClick={exportPages}
              disabled={exporting || selectedExportPages.size === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-pages-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {exportProgress || "Exporting..."}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export {selectedExportPages.size} Pages as ZIP
                </>
              )}
            </Button>
          </div>

          {/* Page Selection */}
          <div className="border border-slate-200 rounded-lg bg-white p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500">
                Select pages to export ({selectedExportPages.size}/{exportablePages.length})
              </span>
              <button
                onClick={toggleAllExportPages}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                data-testid="toggle-all-pages"
              >
                {selectedExportPages.size === exportablePages.length ? "Deselect All" : "Select All"}
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {exportablePages.map(page => (
                <button
                  key={page.page_id}
                  onClick={() => toggleExportPage(page.page_id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                    selectedExportPages.has(page.page_id) 
                      ? "bg-blue-100 text-blue-800 border border-blue-300" 
                      : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"
                  }`}
                  data-testid={`export-page-toggle-${page.page_id}`}
                >
                  {selectedExportPages.has(page.page_id) ? (
                    <CheckSquare className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <Square className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span className="truncate">{page.label}</span>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Available Report Templates */}
      {templates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              Available Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map(template => (
                <div 
                  key={template.template_id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-800">{template.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">
                        {template.date_range === "7d" ? "Last 7 days" : 
                         template.date_range === "30d" ? "Last 30 days" : 
                         template.date_range === "90d" ? "Last 90 days" : template.date_range}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {template.pages?.filter(p => p.enabled !== false).length || 0} sections
                    </Badge>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => generateReport(template)}
                      disabled={generating[template.template_id]}
                      className="flex-1"
                    >
                      {generating[template.template_id] ? (
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Eye className="w-3 h-3 mr-1" />
                      )}
                      Generate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadZip(template.template_id, template.name)}
                      disabled={downloading[template.template_id] === 'zip'}
                    >
                      {downloading[template.template_id] === 'zip' ? (
                        <RefreshCw className="w-3 h-3 animate-spin" />
                      ) : (
                        <FileArchive className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-slate-600" />
            Report History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-slate-300" />
              <p>Loading reports...</p>
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No reports generated yet</p>
              <p className="text-sm mt-1">Generate a report from the templates above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map(report => (
                <div 
                  key={report.report_id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-800">
                        {report.template_name || "Report"}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(report.generated_at)}
                        </span>
                        <span className="text-slate-300">•</span>
                        <span>{getRelativeTime(report.generated_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewReport(report.report_id)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadHtml(report)}
                      disabled={downloading[report.report_id] === 'html'}
                    >
                      {downloading[report.report_id] === 'html' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
